import { create } from 'zustand';
import api from '@/services/api';
import type { SavedPrompt, PromptLimitsResponse } from '@/types';

interface PromptStore {
  // State
  prompts: SavedPrompt[];
  limits: PromptLimitsResponse | null;
  isLoading: boolean;
  error: string | null;
  lastSavedPromptText: string | null; // Track last saved prompt for button visibility
  currentWorkspaceId: string | null; // Track which workspace these prompts belong to

  // Actions
  fetchPrompts: (workspaceId?: string) => Promise<void>;
  fetchLimits: (workspaceId?: string) => Promise<void>;
  savePrompt: (promptText: string, workspaceId?: string) => Promise<SavedPrompt | null>;
  updatePrompt: (id: string, promptText: string, workspaceId?: string) => Promise<void>;
  deletePrompt: (id: string, workspaceId?: string) => Promise<void>;
  reorderPrompts: (promptIds: string[], workspaceId?: string) => Promise<void>;
  setLastSavedPromptText: (text: string | null) => void;
  setCurrentWorkspaceId: (workspaceId: string | null) => void;
  clearError: () => void;
  clearPrompts: () => void; // Clear prompts when switching workspaces
}

export const usePromptStore = create<PromptStore>((set, get) => ({
  prompts: [],
  limits: null,
  isLoading: false,
  error: null,
  lastSavedPromptText: null,
  currentWorkspaceId: null,

  fetchPrompts: async (workspaceId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.getPrompts(workspaceId);
      set({
        prompts: response.prompts,
        currentWorkspaceId: workspaceId || null,
        limits: {
          current_count: response.total_count,
          max_allowed: response.limit,
          can_save_more: response.can_save_more,
          tier: get().limits?.tier || 'basic',
        },
        isLoading: false,
      });
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to fetch prompts';
      set({ error: message, isLoading: false });
    }
  },

  fetchLimits: async (workspaceId?: string) => {
    try {
      const limits = await api.getPromptLimits(workspaceId);
      set({ limits });
    } catch (err: any) {
      console.error('Failed to fetch prompt limits:', err);
    }
  },

  savePrompt: async (promptText: string, workspaceId?: string) => {
    set({ error: null });
    try {
      const newPrompt = await api.savePrompt(promptText, workspaceId);
      const currentPrompts = get().prompts;
      set({
        prompts: [...currentPrompts, newPrompt],
        lastSavedPromptText: promptText,
        limits: get().limits ? {
          ...get().limits!,
          current_count: get().limits!.current_count + 1,
          can_save_more: get().limits!.current_count + 1 < get().limits!.max_allowed,
        } : null,
      });
      return newPrompt;
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to save prompt';
      set({ error: message });
      return null;
    }
  },

  updatePrompt: async (id: string, promptText: string, workspaceId?: string) => {
    set({ error: null });
    try {
      const updated = await api.updatePrompt(id, promptText, workspaceId);
      const prompts = get().prompts.map(p => p.id === id ? updated : p);
      set({ prompts });
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to update prompt';
      set({ error: message });
      throw err;
    }
  },

  deletePrompt: async (id: string, workspaceId?: string) => {
    set({ error: null });
    try {
      await api.deletePrompt(id, workspaceId);
      const prompts = get().prompts.filter(p => p.id !== id);
      // Recalculate display_order
      prompts.forEach((p, i) => { p.display_order = i; });
      set({
        prompts,
        limits: get().limits ? {
          ...get().limits!,
          current_count: Math.max(0, get().limits!.current_count - 1),
          can_save_more: true,
        } : null,
      });
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to delete prompt';
      set({ error: message });
      throw err;
    }
  },

  reorderPrompts: async (promptIds: string[], workspaceId?: string) => {
    // Optimistic update
    const currentPrompts = get().prompts;
    const reorderedPrompts = promptIds
      .map((id, index) => {
        const prompt = currentPrompts.find(p => p.id === id);
        return prompt ? { ...prompt, display_order: index } : null;
      })
      .filter(Boolean) as SavedPrompt[];
    
    set({ prompts: reorderedPrompts });

    try {
      const serverPrompts = await api.reorderPrompts(promptIds, workspaceId);
      set({ prompts: serverPrompts });
    } catch (err: any) {
      // Revert on error
      set({ prompts: currentPrompts });
      const message = err?.response?.data?.detail || 'Failed to reorder prompts';
      set({ error: message });
      throw err;
    }
  },

  setLastSavedPromptText: (text: string | null) => {
    set({ lastSavedPromptText: text });
  },

  setCurrentWorkspaceId: (workspaceId: string | null) => {
    // When workspace changes, clear prompts so they can be refetched
    if (workspaceId !== get().currentWorkspaceId) {
      set({ 
        currentWorkspaceId: workspaceId,
        prompts: [],
        limits: null,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearPrompts: () => {
    set({ 
      prompts: [],
      limits: null,
      currentWorkspaceId: null,
    });
  },
}));
